/**
 * playerEnricher.js
 *
 * Enriches player records by querying the public Wikidata API.
 * Runs entirely in the browser — no server required.
 * CORS is supported by Wikidata via the `origin=*` parameter.
 *
 * Data retrieved per player (when available):
 *   wikidataId  — Wikidata QID (e.g. "Q1426")
 *   name        — canonical Wikidata label (e.g. "Jeline Vandromme")
 *   dob         — date of birth (Unix ms)
 *   height      — height in cm
 *   gender      — "Male" | "Female"
 *   hand        — 0 right, 1 left
 *   country     — { name, code } (ISO 3166-1 alpha-2)
 *   atpId       — ATP player ID (P3997)
 *   wtaId       — WTA player ID (P4168)
 *   photoUrl    — portrait image URL via Wikimedia Commons
 */

const WIKIDATA_API = "https://www.wikidata.org/w/api.php";
const COMMONS_FILE_PATH = "https://commons.wikimedia.org/wiki/Special:FilePath/";

/** Convert a Wikidata time string like "+1981-08-08T00:00:00Z" to Unix ms. */
function wikidataTimeToMs(timeStr) {
  const clean = timeStr.replace(/^\+/, "");
  const d = new Date(clean);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Search Wikidata for a tennis player by name.
 * Returns the QID of the first result whose description contains "tennis", or null.
 */
/**
 * Search Wikidata by name and return the raw candidate list
 * ([{ id, label, description }, ...]).
 */
async function searchEntities(name) {
  if (!name) return [];
  const params = new URLSearchParams({
    action: "wbsearchentities",
    search: name,
    type: "item",
    language: "en",
    uselang: "en",
    limit: "15",
    format: "json",
    origin: "*",
  });
  const resp = await fetch(`${WIKIDATA_API}?${params}`);
  if (!resp.ok) return [];
  const data = await resp.json();
  return (data.search ?? []).map((it) => ({
    id: it.id,
    label: it.label ?? "",
    description: it.description ?? "",
  }));
}

/**
 * Decide whether an entity's claims describe a tennis player. More reliable than
 * matching the free-text description: checks occupation (P106 = tennis player),
 * sport (P641 = tennis), or the presence of an ATP/WTA player ID.
 */
function isTennisPlayerClaims(claims) {
  if (!claims) return false;
  const hasEntity = (pid, id) =>
    (claims[pid] ?? []).some(
      (c) => c?.mainsnak?.datavalue?.value?.id === id,
    );
  return (
    hasEntity("P106", "Q10833314") || // occupation: tennis player
    hasEntity("P641", "Q847") || // sport: tennis
    !!claims.P3997 || // ATP player ID
    !!claims.P4168 // WTA player ID
  );
}

/** Fetch an item's English label and claims by QID. */
async function fetchEntity(qid) {
  const params = new URLSearchParams({
    action: "wbgetentities",
    ids: qid,
    props: "labels|claims",
    languages: "en",
    format: "json",
    origin: "*",
  });
  const resp = await fetch(`${WIKIDATA_API}?${params}`);
  if (!resp.ok) return null;
  const data = await resp.json();
  const ent = data.entities?.[qid];
  if (!ent) return null;
  return { label: ent.labels?.en?.value ?? "", claims: ent.claims ?? null };
}

/** Extract the first datavalue for a claim property. */
function claimValue(claims, pid) {
  return claims?.[pid]?.[0]?.mainsnak?.datavalue?.value ?? null;
}

/** Extract the QID of the first entity-valued claim (e.g. country, gender). */
function claimEntityId(claims, pid) {
  return claimValue(claims, pid)?.id ?? null;
}

/** Fetch a country's English name and ISO 3166-1 alpha-2 code by QID. */
async function fetchCountry(qid) {
  const params = new URLSearchParams({
    action: "wbgetentities",
    ids: qid,
    props: "labels|claims",
    languages: "en",
    format: "json",
    origin: "*",
  });
  const resp = await fetch(`${WIKIDATA_API}?${params}`);
  if (!resp.ok) return null;
  const data = await resp.json();
  const ent = data.entities?.[qid];
  if (!ent) return null;
  const name = ent.labels?.en?.value ?? "";
  const code = ent.claims?.P297?.[0]?.mainsnak?.datavalue?.value ?? "";
  return { name, code: code.toUpperCase() };
}

/**
 * Full-text search for a tennis player via CirrusSearch (`list=search`). Unlike
 * `wbsearchentities`, CirrusSearch folds diacritics — so "Banuls" matches
 * "Bañuls" — and `haswbstatement` lets us require a tennis occupation/sport in
 * the same query. Returns candidate QIDs.
 */
async function searchTennisFulltext(name) {
  if (!name) return [];
  const params = new URLSearchParams({
    action: "query",
    list: "search",
    // P106=Q10833314 (tennis player) OR P641=Q847 (sport: tennis)
    srsearch: `${name} haswbstatement:P106=Q10833314|P641=Q847`,
    srlimit: "15",
    srnamespace: "0",
    format: "json",
    origin: "*",
  });
  const resp = await fetch(`${WIKIDATA_API}?${params}`);
  if (!resp.ok) return [];
  const data = await resp.json();
  return (data.query?.search ?? []).map((r) => ({
    id: r.title,
    label: "",
    description: "",
  }));
}

/**
 * Given candidate items (most tennis-likely first), fetch each entity and return
 * the first that is confirmed a tennis player, as { qid, claims, label }.
 */
async function firstTennisMatch(candidates) {
  for (const c of candidates) {
    const ent = await fetchEntity(c.id);
    if (ent && isTennisPlayerClaims(ent.claims)) {
      return { qid: c.id, claims: ent.claims, label: ent.label || c.label };
    }
  }
  return null;
}

/**
 * Search Wikidata for a tennis player and return enrichment data.
 *
 * @param {string} firstName
 * @param {string} lastName
 * @returns {Promise<object|null>} Enrichment fields, or null if not found.
 */
export async function enrichPlayerFromWikidata(firstName, lastName) {
  try {
    const name = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    if (!name) return null;

    // 1. Fast, label-based search. Check the most tennis-looking candidates
    //    first (description mentions "tennis").
    const candidates = await searchEntities(name);
    candidates.sort(
      (a, b) =>
        (b.description.toLowerCase().includes("tennis") ? 1 : 0) -
        (a.description.toLowerCase().includes("tennis") ? 1 : 0),
    );
    let match = await firstTennisMatch(candidates);

    // 2. Fallback: diacritic-folding full-text search (finds "Bañuls" for
    //    "Banuls") restricted to tennis players.
    if (!match) {
      match = await firstTennisMatch(await searchTennisFulltext(name));
    }
    if (!match) return null;

    const { qid, claims, label } = match;

    // Wikidata's canonical name — lets callers fill in a missing first/last name.
    const result = { wikidataId: qid };
    if (label) result.name = label;

    // Date of birth — P569
    const dobVal = claimValue(claims, "P569");
    if (dobVal?.time) {
      const ms = wikidataTimeToMs(dobVal.time);
      if (ms) result.dob = ms;
    }

    // Height — P2048 (stored in metres or cm depending on unit)
    const heightVal = claimValue(claims, "P2048");
    if (heightVal?.amount) {
      const h = parseFloat(heightVal.amount);
      const unit = heightVal.unit ?? "";
      // Q11573 = metre, Q174728 = centimetre
      result.height = unit.includes("Q11573") ? Math.round(h * 100) : Math.round(h);
    }

    // ATP player ID — P3997
    const atpId = claimValue(claims, "P3997");
    if (atpId) result.atpId = String(atpId);

    // WTA player ID — P4168
    const wtaId = claimValue(claims, "P4168");
    if (wtaId) result.wtaId = String(wtaId);

    // Gender — P21 (Q6581097 male, Q6581072 female)
    const genderId = claimEntityId(claims, "P21");
    if (genderId === "Q6581097") result.gender = "Male";
    else if (genderId === "Q6581072") result.gender = "Female";

    // Handedness — P552 (Q789447 right, Q1758281 left, Q27937703 ambidextrous)
    // Stored as 0 = right, 1 = left, 2 = unknown to match the app's convention.
    const handId = claimEntityId(claims, "P552");
    if (handId === "Q789447") result.hand = 0;
    else if (handId === "Q1758281") result.hand = 1;

    // Country of citizenship — P27 → resolve to { name, code }
    const countryId = claimEntityId(claims, "P27");
    if (countryId) {
      const country = await fetchCountry(countryId);
      if (country && (country.name || country.code)) result.country = country;
    }

    // Portrait photo — P18 (filename on Wikimedia Commons)
    const imageFile = claimValue(claims, "P18");
    if (imageFile) {
      const encoded = encodeURIComponent(imageFile.replace(/ /g, "_"));
      result.photoUrl = `${COMMONS_FILE_PATH}${encoded}?width=300`;
    }

    return result;
  } catch (err) {
    console.warn("Player enrichment failed for", firstName, lastName, ":", err.message);
    return null;
  }
}
